import { Check, ChevronDown, X } from 'lucide-react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cn } from '@Utils';

const Combobox = ComboboxPrimitive.Root;

const ComboboxInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => {
  return (
    <div className="relative flex h-10 w-full items-center rounded-md border border-input bg-white ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      <ComboboxPrimitive.Input
        className={cn(
          'flex h-full w-full rounded-md bg-transparent px-3 py-2 pr-16 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          className,
        )}
        {...props}
      />
      <div className="absolute right-2 flex items-center gap-1">
        <ComboboxPrimitive.Clear
          className="rounded-sm p-1 text-gray-400 outline-none transition-colors hover:text-gray-700 data-[empty]:hidden"
          aria-label="Clear">
          <X className="size-3.5" />
        </ComboboxPrimitive.Clear>
        <ComboboxPrimitive.Trigger
          className="rounded-sm p-1 text-gray-400 outline-none transition-colors hover:text-gray-700"
          aria-label="Open">
          <ChevronDown className="size-4" />
        </ComboboxPrimitive.Trigger>
      </div>
    </div>
  );
};

const ComboboxContent = ({
  className,
  positionerClassName,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset'> & {
    /** Tailwind classes forwarded to the Positioner (e.g. `z-[140]` to layer
     * the dropdown above a high-stacking dialog). */
    positionerClassName?: string;
  }) => {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        className={cn('z-50', positionerClassName)}>
        <ComboboxPrimitive.Popup
          className={cn(
            'max-h-96 w-[var(--anchor-width)] origin-[var(--transform-origin)] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
            'data-[open]:animate-in data-[closed]:animate-out data-[open]:fade-in-0 data-[closed]:fade-out-0 data-[open]:zoom-in-95 data-[closed]:zoom-out-95',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

const ComboboxList = ({ className, ...props }: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List className={cn('max-h-80 overflow-y-auto p-1 data-[empty]:p-0', className)} {...props} />
  );
};

const ComboboxItem = ({ className, children, ...props }: ComboboxPrimitive.Item.Props) => {
  return (
    <ComboboxPrimitive.Item
      className={cn(
        'relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none select-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}>
      {children}
      <ComboboxPrimitive.ItemIndicator className="absolute right-2 flex size-4 items-center justify-center">
        <Check className="size-4" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
};

const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      className={cn('py-6 text-center text-sm text-muted-foreground empty:hidden empty:py-0', className)}
      {...props}
    />
  );
};

export { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList };
